import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { VISA_COUNTRIES, IMAGES } from '@/lib/constants';
import { CheckCircle, Clock, FileText, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const visaDetails = {
  uae: {
    name: "Dubai/UAE",
    flag: "🇦🇪",
    image: IMAGES.dubai,
    intro: "The United Arab Emirates is one of the most popular destinations for Indian travelers.",
    types: [
      { name: "Tourist Visa (14 Days)", processing: "3-5 Working Days" },
      { name: "Tourist Visa (30 Days)", processing: "3-5 Working Days" },
      { name: "Transit Visa", processing: "2-3 Working Days" },
    ],
    documents: [
      "Valid passport with 6+ months validity",
      "Recent passport-size photographs",
      "Confirmed return ticket",
      "Hotel booking confirmation",
      "Bank statement (last 3 months)",
    ],
    whyOsaka: ["Express processing available", "98% approval rate", "Document assistance"],
  },
  uk: {
    name: "United Kingdom",
    flag: "🇬🇧",
    image: IMAGES.airport,
    intro: "Planning to visit the UK? Our experts ensure your application meets all requirements.",
    types: [
      { name: "Standard Visitor Visa", processing: "15-20 Working Days" },
      { name: "Business Visitor Visa", processing: "15-20 Working Days" },
    ],
    documents: [
      "Valid passport with 6+ months validity",
      "UK visa application form",
      "Bank statements (last 6 months)",
      "Employment letter / Business documents",
    ],
    whyOsaka: ["VFS appointment assistance", "Mock interview preparation", "High approval rate"],
  },
  usa: {
    name: "United States",
    flag: "🇺🇸",
    image: IMAGES.corporateTravel,
    intro: "The US visa process requires meticulous documentation and interview preparation.",
    types: [
      { name: "B1/B2 Tourist/Business Visa", processing: "Varies" },
      { name: "F1 Student Visa", processing: "Varies" },
    ],
    documents: ["Valid passport", "DS-160 confirmation page", "Visa fee receipt", "Photographs"],
    whyOsaka: ["DS-160 assistance", "Interview scheduling", "Mock interview sessions"],
  },
  canada: {
    name: "Canada",
    flag: "🇨🇦",
    image: IMAGES.studyAbroad,
    intro: "Canada welcomes millions of visitors each year.",
    types: [
      { name: "Tourist Visa", processing: "20-30 Working Days" },
      { name: "Study Permit", processing: "4-8 Weeks" },
    ],
    documents: ["Valid passport", "Completed application forms", "Proof of funds"],
    whyOsaka: ["Expert guidance", "Biometric appointment assistance", "Application tracking"],
  },
  schengen: {
    name: "Schengen Area",
    flag: "🇪🇺",
    image: IMAGES.beach,
    intro: "A single Schengen visa gives you access to 27 European countries.",
    types: [
      { name: "Short-Stay Visa (C Type)", processing: "15-20 Working Days" },
      { name: "Business Visa", processing: "15-20 Working Days" },
    ],
    documents: ["Valid passport", "Application form", "Travel insurance", "Flight itinerary"],
    whyOsaka: ["Embassy selection guidance", "VFS appointment booking", "Complete documentation"],
  },
  ireland: {
    name: "Ireland",
    flag: "🇮🇪",
    image: IMAGES.airport,
    intro: "Ireland offers a unique visa process separate from the Schengen area.",
    types: [
      { name: "Short Stay Visa", processing: "10-15 Working Days" },
      { name: "Study Visa", processing: "4-8 Weeks" },
    ],
    documents: ["Valid passport", "Completed online application", "Photographs", "Travel insurance"],
    whyOsaka: ["Online application assistance", "Document review", "Status tracking"],
  },
};

export default function VisaServicesPage() {
  const { country } = useParams();
  const visa = visaDetails[country] || visaDetails.uae;

  return (
    <div className="page-transition">
      <PageHero title={`${visa.name} Visa Services`} subtitle="Expert visa assistance" backgroundImage={visa.image} />

      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <span className="text-6xl">{visa.flag}</span>
              <div>
                <h2 className="text-3xl font-bold font-['Playfair_Display'] text-[#0A1F3D]">{visa.name} Visa</h2>
                <p className="text-[#D4AF37]">Expert Processing by Osaka Group</p>
              </div>
            </div>
            <p className="text-[#4A5568] text-lg">{visa.intro}</p>
          </div>
        </div>
      </section>

      <section data-testid="visa-types" className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <SectionHeader overline="Visa Categories" title="Available Visa Types" />
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {visa.types.map((type, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-6 border border-slate-200">
                <h3 className="font-bold text-[#0A1F3D] mb-2">{type.name}</h3>
                <div className="flex items-center gap-2 text-sm text-[#4A5568]">
                  <Clock size={16} className="text-[#D4AF37]" />
                  <span>{type.processing}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="documents-required" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <SectionHeader overline="Checklist" title="Documents Required" centered={false} />
              <ul className="space-y-4">
                {visa.documents.map((doc, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FileText size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5568]">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-[#F5F5F5] p-8">
              <h3 className="text-2xl font-bold font-['Playfair_Display'] text-[#0A1F3D] mb-6">Apply Today</h3>
              <p className="text-[#4A5568] mb-8">Ready to start your visa application?</p>
              <Link to="/contact">
                <Button data-testid="visa-apply-btn" className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] py-6 rounded-none font-semibold">
                  Start Application <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F5F5F5]">
        <div className="container-custom">
          <SectionHeader overline="Explore More" title="Other Countries" />
          <div className="flex flex-wrap justify-center gap-4">
            {VISA_COUNTRIES.filter(c => c.id !== country).map((c) => (
              <Link key={c.id} to={`/visa/${c.id}`} className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 hover:border-[#D4AF37]">
                <span className="text-2xl">{c.flag}</span>
                <span className="font-medium text-[#0A1F3D]">{c.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
